import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { SEZIONI_GUIDA } from './contenuti';

interface GuidaMailingProps {
  aperta: boolean;
  onChiudi: (aperta: boolean) => void;
  /** La pagina da cui e' stata aperta: la guida parte da li'. */
  percorsoCorrente?: string;
}

/**
 * La guida di Mailing.
 *
 * Si apre sulla sezione della pagina in cui ci si trova: chi la consulta ha un
 * dubbio su quello che sta facendo adesso, non sta leggendo un manuale.
 */
export function GuidaMailing({ aperta, onChiudi, percorsoCorrente }: GuidaMailingProps) {
  const [sezioneId, setSezioneId] = useState(SEZIONI_GUIDA[0].id);

  useEffect(() => {
    if (!aperta) return;
    const pertinente = SEZIONI_GUIDA.find(
      (s) => s.percorso && percorsoCorrente?.startsWith(s.percorso)
    );
    setSezioneId(pertinente?.id ?? SEZIONI_GUIDA[0].id);
  }, [aperta, percorsoCorrente]);

  // Aprendo, il fuoco della tastiera andrebbe sul primo bottone dell'indice,
  // che si illumina come se fosse la sezione scelta: due voci accese insieme.
  // Lo si porta sul testo, che e' anche quello che si vuole leggere.
  const testo = useRef<HTMLElement>(null);

  const sezione = SEZIONI_GUIDA.find((s) => s.id === sezioneId) ?? SEZIONI_GUIDA[0];
  const indice = SEZIONI_GUIDA.findIndex((s) => s.id === sezione.id);
  const successiva = SEZIONI_GUIDA[indice + 1];

  return (
    <Dialog open={aperta} onOpenChange={onChiudi}>
      <DialogContent
        className="max-w-4xl sm:h-[82vh] flex flex-col overflow-y-hidden gap-0"
        onOpenAutoFocus={(evento) => {
          evento.preventDefault();
          testo.current?.focus();
        }}
      >
        <DialogHeader className="flex-shrink-0 pb-3 border-b border-gray-100">
          <DialogTitle>Come funziona Mailing</DialogTitle>
          <p className="text-xs text-neutral-500">
            Una spiegazione breve di cosa fa ogni pagina e delle poche regole da rispettare.
          </p>
        </DialogHeader>

        <DialogBody className="pt-3">
          <div className="grid gap-5 lg:grid-cols-[210px_minmax(0,1fr)] h-full">
            {/* L'indice: sopra su schermo stretto, di fianco su schermo largo. */}
            <nav className="flex gap-1.5 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
              {SEZIONI_GUIDA.map((s) => {
                const Icona = s.icona;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSezioneId(s.id)}
                    className={cn(
                      'flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-colors flex-shrink-0 lg:w-full',
                      sezioneId === s.id
                        ? 'border-primary-400 bg-primary-50/60 text-primary-800'
                        : 'border-transparent text-neutral-600 hover:bg-gray-50'
                    )}
                  >
                    <Icona className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="text-xs font-medium whitespace-nowrap lg:whitespace-normal">
                      {s.titolo}
                    </span>
                  </button>
                );
              })}
            </nav>

            <article
              ref={testo}
              tabIndex={-1}
              className="min-w-0 outline-none lg:border-l lg:border-gray-100 lg:pl-5"
            >
              <h3 className="text-base font-semibold text-gray-900 mb-3">{sezione.titolo}</h3>
              {sezione.contenuto}
            </article>
          </div>
        </DialogBody>

        <DialogFooter className="flex-shrink-0 border-t border-gray-100 pt-3">
          {successiva && (
            <span className="text-xs text-neutral-500 sm:mr-auto sm:self-center">
              Poi: {successiva.titolo}
            </span>
          )}
          {successiva && (
            <Button variant="outline" onClick={() => setSezioneId(successiva.id)}>
              Avanti
            </Button>
          )}
          <Button
            onClick={() => onChiudi(false)}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            Ho capito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
